import React, { useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Paper,
  Popper,
  Typography,
  ClickAwayListener,
} from '@mui/material';
import { InputUI } from '../Interface';
import useUsers from '../../context/UsersContext';

const CommentMessageField = ({ users, value, onChange, mentionedUserState }) => {
  const { user: currentUser } = useUsers();
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);

  const [mentionData, setMentionData] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const mentionedUserIds = mentionedUserState?.mentionedUserIds ?? [];
  const setMentionedUserIds = mentionedUserState?.setMentionedUserIds;

  const getUserName = (user) => user.nickName;

  const filteredUsers = useMemo(() => {
    if (!mentionData) {
      return [];
    }

    const searchValue = mentionData.search.toLowerCase();

    return users.filter(
      (listedUser) =>
        listedUser.userId !== currentUser.userId &&
        !mentionedUserIds.includes(listedUser.userId) &&
        getUserName(listedUser).toLowerCase().includes(searchValue)
    );
  }, [users, mentionData, mentionedUserIds, currentUser.userId]);

  const isMentionOpen = Boolean(mentionData);

  const findMentionAtCursor = (message, cursorPosition) => {
    const textBeforeCursor = message.slice(0, cursorPosition);
    const match = textBeforeCursor.match(/(^|\s)@([^\s@]*)$/);

    if (!match) {
      return null;
    }

    const search = match[2] ?? '';
    const start = cursorPosition - search.length - 1;

    return {
      search,
      start,
      end: cursorPosition,
    };
  };

  const handleChange = (event) => {
    if (!mentionedUserState) {
      onChange?.(event);
      return;
    }

    const newValue = event.target.value;
    const cursorPosition = event.target.selectionStart ?? newValue.length;

    const remainingUserIds = mentionedUserIds.filter((userId) => {
      const user = users.find((item) => item.userId === userId);

      if (!user) {
        return false;
      }

      return newValue.includes(`@${getUserName(user)}`);
    });

    setMentionedUserIds(remainingUserIds);
    onChange?.(event);

    const currentMention = findMentionAtCursor(newValue, cursorPosition);

    setMentionData(currentMention);
    setActiveIndex(0);
  };

  const handleSelectUser = (user) => {
    if (!mentionData) {
      return;
    }

    const userName = getUserName(user);

    if (mentionedUserIds.includes(user.userId)) {
      return;
    }

    const textBeforeMention = value.slice(0, mentionData.start);
    const textAfterMention = value.slice(mentionData.end);
    const insertedMention = `@${userName} `;

    const newValue = textBeforeMention + insertedMention + textAfterMention;

    const newCursorPosition = textBeforeMention.length + insertedMention.length;

    setMentionedUserIds((prev) => [...prev, user.userId]);

    onChange?.({
      target: {
        name: 'message',
        value: newValue,
      },
    });

    setMentionData(null);
    setActiveIndex(0);

    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(newCursorPosition, newCursorPosition);
    });
  };

  return (
    <Box ref={wrapperRef} sx={{ position: 'relative' }}>
      <InputUI
        inputRef={inputRef}
        name="message"
        placeholder="Hozzászólás írása..."
        multiline
        rows={2}
        autoFocus
        value={value}
        onChange={handleChange}
      />

      <Popper
        open={isMentionOpen}
        anchorEl={wrapperRef.current}
        placement="bottom-start"
        sx={{
          zIndex: 1500,
          width: wrapperRef.current?.offsetWidth,
          maxWidth: 400,
        }}
      >
        <ClickAwayListener onClickAway={() => setMentionData(null)}>
          <Paper
            elevation={6}
            sx={{
              mt: 0.5,
              maxHeight: 280,
              overflowY: 'auto',
            }}
          >
            {filteredUsers.length ? (
              filteredUsers.map((user, index) => {
                const userName = getUserName(user);

                return (
                  <ListItemButton
                    key={user.userId}
                    selected={index === activeIndex}
                    onMouseDown={(event) => {
                      event.preventDefault();
                    }}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => handleSelectUser(user)}
                  >
                    <ListItemAvatar>
                      <Avatar src={user.avatar}>{userName.charAt(0).toUpperCase()}</Avatar>
                    </ListItemAvatar>

                    <ListItemText primary={userName} secondary={user.email ?? user.roleName} />
                  </ListItemButton>
                );
              })
            ) : (
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                  Nincs találat
                </Typography>
              </Box>
            )}
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

export default CommentMessageField;
